import React, { useState } from 'react'
import { Box, Tooltip, Typography, useTheme, alpha } from '@mui/material'
import { TrackerState } from '../../types/gotypes'
import { usePrefersReducedMotion } from './motion'
import { TRACKER_MAX_WIDTH } from './atoms'

// TrackerHeader renders the co-branded programme header: customer logo +
// Platinum wordmark on the left, last-updated stamp on the right.
export const TrackerHeader: React.FC<{ tracker: TrackerState }> = ({ tracker }) => {
  const theme = useTheme()
  const { branding } = tracker
  const [logoOk, setLogoOk] = useState(true)
  const reduced = usePrefersReducedMotion()

  const primary = branding.primaryColor || theme.palette.primary.main

  const lastUpdated = tracker.lastUpdated
    ? new Date(tracker.lastUpdated).toLocaleString(undefined, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : ''

  return (
    <Box sx={{ position: 'relative' }}>
      <Box sx={{ height: 3, bgcolor: primary }} />
      <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
      <Box
        sx={{
          maxWidth: TRACKER_MAX_WIDTH,
          mx: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 2, md: 4 },
          py: 2.5,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {logoOk && branding.logoUrl ? (
            <Box
              component="img"
              src={branding.logoUrl}
              alt={branding.customerName}
              onError={() => setLogoOk(false)}
              sx={{ height: 34, width: 'auto' }}
            />
          ) : (
            <Typography sx={{ fontSize: 26, fontWeight: 800, color: primary, lineHeight: 1 }}>
              {branding.customerName}
            </Typography>
          )}
          <Box sx={{ width: '1px', height: 30, bgcolor: theme.palette.divider }} />
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: 2, color: theme.palette.text.secondary }}>
              PLATINUM
            </Typography>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1.1 }}>
              {tracker.subtitle || 'Programme Tracker'}
            </Typography>
          </Box>
        </Box>

        {lastUpdated && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Tooltip arrow title="Last updated">
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: theme.palette.success.main,
                  flexShrink: 0,
                  ...(!reduced && {
                    animation: 'trackerLive 2.4s ease-in-out infinite',
                    '@keyframes trackerLive': {
                      '0%, 100%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.success.main, 0.55)}` },
                      '50%': { boxShadow: `0 0 0 5px ${alpha(theme.palette.success.main, 0)}` },
                    },
                  }),
                }}
              />
            </Tooltip>
            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 1, color: theme.palette.text.disabled }}>
                LAST UPDATED
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: theme.palette.text.secondary }}>
                {lastUpdated}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
      </Box>
      <Box sx={{ maxWidth: TRACKER_MAX_WIDTH, mx: 'auto', px: { xs: 2, md: 4 }, pt: 3, pb: 1 }}>
        <Typography
          sx={{
            fontSize: { xs: 26, md: 32 },
            fontWeight: 800,
            color: primary,
            display: 'inline-block',
          }}
        >
          {tracker.title}
        </Typography>
        <Typography sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
          Programme tracker
        </Typography>
      </Box>
    </Box>
  )
}

export default TrackerHeader
